import type { IAndroidBrightnessStatus } from '@/helpers/parseAndroidBrightnessStatus'

/**
 * Whether the first-run "Modify system settings" (WRITE_SETTINGS) prompt applies to this screen.
 *
 * - Same audience as the Accessibility gate: a Device Owner that is not yet bound to a tenant, so an
 *   already-registered production screen is never interrupted after an APK/Web OTA.
 * - Only when the device can steer its own backlight (`brightnessControl: supported`); HDMI boxes and
 *   Android TV report `unsupported` and must never see it.
 * - Only while the app-op is not granted **and the device cannot already write the system brightness by other
 *   means**: a Device Owner on Android 9+ does that through `DevicePolicyManager.setSystemSetting` and reports
 *   `brightnessMethod: system` without `WRITE_SETTINGS`, so it is never asked. The prompt therefore only
 *   remains for a Device Owner where that path is unavailable or was refused (older Android, OEM).
 * - A player whose APK predates the brightness commands reports no status (`null`) and is not prompted.
 */
export function isWriteSettingsSetupEligible(opts: {
  deviceOwner: boolean
  tenantRegistered: boolean
  brightness: Pick<IAndroidBrightnessStatus, 'brightnessControl' | 'brightnessMethod' | 'writeSettingsGranted'> | null
}): boolean {
  if (!opts.deviceOwner || opts.tenantRegistered || opts.brightness == null) {
    return false
  }
  return opts.brightness.brightnessControl === 'supported'
    && opts.brightness.writeSettingsGranted === false
    && opts.brightness.brightnessMethod !== 'system'
}
